/**
 * Organization Audit Log Tab
 *
 * Shows audit log entries scoped to this organization.
 */

import { useState } from 'react';
import { AdminCard } from '@/components/admin/AdminShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Search,
  ChevronDown,
  ChevronRight,
  User,
  Shield,
  Building2,
  FileText,
  Settings,
  Play,
  Download,
} from 'lucide-react';
import { Org, AuditLogEntry, AdminActionType } from '@/integrations/firebase/types';
import { format } from 'date-fns';

interface OrgAuditTabProps {
  org: Org;
}

// Audit logs - will be loaded from Firestore
// TODO: Replace with real Firestore query for org-scoped audit logs
const auditLogs: AuditLogEntry[] = [];

const ACTION_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  CREATE_ORG: Building2,
  UPDATE_ORG: Building2,
  INVITE_USER: User,
  DISABLE_USER: User,
  ENABLE_USER: User,
  CHANGE_USER_ROLE: User,
  SET_PLAN: Settings,
  SET_SCOPE: Settings,
  IMPERSONATE_ORG: Shield,
  EXIT_IMPERSONATION: Shield,
  CREATE_JOB_AS_ORG: FileText,
  RUN_CHECK_AS_ORG: Play,
  RERUN_CHECK: Play,
  DOWNLOAD_DEBUG_BUNDLE: Download,
};

const ACTION_COLORS: Record<string, string> = {
  CREATE_ORG: 'text-green-400',
  UPDATE_ORG: 'text-blue-400',
  INVITE_USER: 'text-green-400',
  DISABLE_USER: 'text-red-400',
  ENABLE_USER: 'text-green-400',
  SET_PLAN: 'text-purple-400',
  SET_SCOPE: 'text-purple-400',
  IMPERSONATE_ORG: 'text-amber-400',
  EXIT_IMPERSONATION: 'text-amber-400',
  CREATE_JOB_AS_ORG: 'text-blue-400',
  RUN_CHECK_AS_ORG: 'text-blue-400',
  DOWNLOAD_DEBUG_BUNDLE: 'text-gray-400',
};

export function OrgAuditTab({ org }: OrgAuditTabProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());

  // Filter logs
  const filteredLogs = auditLogs.filter((log) => {
    const matchesSearch =
      log.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.actorEmail.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAction =
      actionFilter === 'all' || log.actionType === actionFilter;
    return matchesSearch && matchesAction;
  });

  const toggleExpanded = (logId: string) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedLogs(newExpanded);
  };

  const getActionIcon = (actionType: AdminActionType) => {
    const Icon = ACTION_ICONS[actionType] || FileText;
    const color = ACTION_COLORS[actionType] || 'text-gray-400';
    return <Icon className={`h-4 w-4 ${color}`} />;
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'superadmin':
        return (
          <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/40">
            Superadmin
          </Badge>
        );
      case 'admin':
        return (
          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/40">
            Admin
          </Badge>
        );
      case 'support':
        return (
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/40">
            Support
          </Badge>
        );
      case 'dev':
        return (
          <Badge className="bg-green-500/20 text-green-400 border-green-500/40">
            Dev
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="border-gray-600 text-gray-400">
            {role}
          </Badge>
        );
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-white mb-1">Audit Log</h2>
        <p className="text-sm text-gray-400">
          View all admin actions taken on this organization
        </p>
      </div>

      {/* Filters */}
      <AdminCard>
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search by description or actor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white placeholder:text-gray-500"
            />
          </div>

          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-48 bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white">
              <SelectValue placeholder="Action Type" />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(220,22%,12%)] border-[hsl(220,15%,25%)]">
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="IMPERSONATE_ORG">Support Mode</SelectItem>
              <SelectItem value="CREATE_JOB_AS_ORG">Job Creation</SelectItem>
              <SelectItem value="RUN_CHECK_AS_ORG">Run Check</SelectItem>
              <SelectItem value="SET_PLAN">Plan Changes</SelectItem>
              <SelectItem value="SET_SCOPE">Scope Changes</SelectItem>
              <SelectItem value="INVITE_USER">User Invites</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </AdminCard>

      {/* Audit Log List */}
      <div className="space-y-2">
        {filteredLogs.length === 0 ? (
          <AdminCard>
            <p className="text-center text-gray-500 py-8">
              No audit log entries found
            </p>
          </AdminCard>
        ) : (
          filteredLogs.map((log) => (
            <Collapsible
              key={log.id}
              open={expandedLogs.has(log.id)}
              onOpenChange={() => toggleExpanded(log.id)}
            >
              <AdminCard className="p-0">
                <CollapsibleTrigger asChild>
                  <button className="w-full p-4 flex items-center gap-4 text-left hover:bg-[hsl(220,15%,12%)] transition-colors">
                    <div className="h-8 w-8 rounded-lg bg-[hsl(220,15%,15%)] flex items-center justify-center">
                      {getActionIcon(log.actionType)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">
                        {log.description}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">
                          {log.actorEmail}
                        </span>
                        {getRoleBadge(log.actorRole)}
                      </div>
                    </div>

                    <div className="text-right">
                      <p className="text-sm text-gray-400">
                        {log.timestamp
                          ? format(log.timestamp.toDate(), 'MMM d, h:mm a')
                          : '-'}
                      </p>
                    </div>

                    {log.diff ? (
                      expandedLogs.has(log.id) ? (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-500" />
                      )
                    ) : (
                      <div className="w-4" />
                    )}
                  </button>
                </CollapsibleTrigger>

                {log.diff && (
                  <CollapsibleContent>
                    <div className="px-4 pb-4 pt-0 border-t border-[hsl(220,15%,20%)]">
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <p className="text-xs text-gray-500 mb-2">Before</p>
                          <pre className="text-xs text-red-400 bg-[hsl(220,15%,12%)] p-3 rounded-md overflow-auto">
                            {JSON.stringify(log.diff.before, null, 2)}
                          </pre>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-2">After</p>
                          <pre className="text-xs text-green-400 bg-[hsl(220,15%,12%)] p-3 rounded-md overflow-auto">
                            {JSON.stringify(log.diff.after, null, 2)}
                          </pre>
                        </div>
                      </div>

                      {log.userAgent && (
                        <p className="text-xs text-gray-600 mt-4">
                          User Agent: {log.userAgent}
                        </p>
                      )}
                    </div>
                  </CollapsibleContent>
                )}
              </AdminCard>
            </Collapsible>
          ))
        )}
      </div>
    </div>
  );
}
